import type { TTimelineItem } from "../../types";
import { collectRenderableSegments, type TRenderableSegment } from "../../utils/tree";
import type { IntervalResult, TrackingActivity, TrackingInterval, TrackingStage } from "../contracts";
import { buildBoxCoverage, type BoxCoverage } from "./coverage";
import type { TrackingSelectionIndex } from "./selection";
import { DecodedGapEvidence } from "./timeline-gap-bridges";
import { isRecoveryPending } from "../recovery/contracts";

export interface TimelineStatusRun {
  start: number;
  end: number;
  /** Bridged spans join the timeline visually; their video boxes are still unavailable. */
  state: "ready" | "pending" | "unavailable" | "bridged";
  reason?: string;
}

export interface TimelineTrackSegment {
  start: number;
  end: number;
  runs: readonly TimelineStatusRun[];
}

export interface TrackingTimeline {
  /** Shared by incremental publications; replaced on source, GT, or settings reset. */
  revision?: object;
  coverage: BoxCoverage;
  segments: ReadonlyMap<string, TimelineTrackSegment>;
}

export interface TrackingTimelineInputs {
  items: readonly TTimelineItem[];
  index: TrackingSelectionIndex;
  intervals: readonly TrackingInterval[];
  activity: TrackingActivity;
  errors: ReadonlyMap<string, string>;
  durationSeconds: number;
}

type Status = Pick<TimelineStatusRun, "state" | "reason"> & { keepReady?: boolean };
type StatusRange = Status & { start: number; end: number };

const STAGES: Record<TrackingStage, string> = {
  decoding: "Decoding video frames.",
  sparse: "Running sparse optical flow.",
  "nano-initializing": "Loading NanoTrack.",
  nano: "Running NanoTrack.",
  finalizing: "Combining predictions.",
};

function defaultStatus(activity: TrackingActivity, errors: ReadonlyMap<string, string>): Status {
  if (!activity.enabled) return { state: "unavailable", reason: "Tracking is disabled." };
  const error = errors.get("worker") ?? activity.unavailableReason;
  return { state: "unavailable", reason: error ?? "No eligible pair of GT boxes." };
}

/** Unfinished intervals stay wholly pending; terminal states reveal their exact accepted coverage. */
function projectRuns(
  start: number,
  end: number,
  ready: readonly { start: number; end: number }[],
  intervals: readonly StatusRange[],
  fallback: Status,
): TimelineStatusRun[] {
  type Boundary = { time: number; readyDelta?: number; interval?: StatusRange; entering?: boolean };
  const boundaries: Boundary[] = [{ time: start }, { time: end }];
  for (const range of ready) {
    const from = Math.max(start, range.start);
    const to = Math.min(end, range.end);
    if (from < to) boundaries.push({ time: from, readyDelta: 1 }, { time: to, readyDelta: -1 });
  }
  for (const interval of intervals) {
    const from = Math.max(start, interval.start);
    const to = Math.min(end, interval.end);
    if (from < to) boundaries.push({ time: from, interval, entering: true }, { time: to, interval, entering: false });
  }
  boundaries.sort((a, b) => a.time - b.time);
  let available = 0;
  // Eligible intervals join neighboring GTs, so their interiors do not overlap.
  // A set also handles coincident boundaries without relying on event ordering.
  const active = new Set<StatusRange>();
  const runs: TimelineStatusRun[] = [];
  let i = 0;
  while (i < boundaries.length) {
    const from = boundaries[i]!.time;
    do {
      const boundary = boundaries[i++]!;
      available += boundary.readyDelta ?? 0;
      if (boundary.interval) {
        if (boundary.entering) active.add(boundary.interval);
        else active.delete(boundary.interval);
      }
    } while (i < boundaries.length && boundaries[i]!.time === from);
    const to = boundaries[i]?.time;
    if (to === undefined || !(from < to)) continue;
    let status = fallback;
    for (const interval of active) status = interval;
    if (available > 0 && (status.state !== "pending" || status.keepReady)) status = { state: "ready" };
    const previous = runs.at(-1);
    if (previous && previous.state === status.state && previous.reason === status.reason) previous.end = to;
    else runs.push({ start: from, end: to, state: status.state, ...(status.reason ? { reason: status.reason } : {}) });
  }
  return runs;
}

/** Stable GT envelopes and terminal/pending status; never changes which video boxes are selected. */
export function buildTrackingTimeline({
  items,
  index,
  intervals,
  activity,
  errors,
  durationSeconds,
}: TrackingTimelineInputs): TrackingTimeline {
  const coverage = buildBoxCoverage(index, durationSeconds);
  const duration = Number.isFinite(durationSeconds) && durationSeconds > 0 ? durationSeconds : Infinity;
  const fallback = defaultStatus(activity, errors);
  const terminalReason = !activity.enabled ? fallback.reason : (errors.get("worker") ?? activity.unavailableReason);
  const tails = new Map<string, number>();
  const lastAnchors = new Map<string, number>();
  for (const { start, end, value } of index.raw.ranges) {
    const source = value.segment;
    const id = source.interactionId ?? source.id;
    if (!coverage.has(id)) continue;
    if (Number.isFinite(start)) lastAnchors.set(id, Math.max(lastAnchors.get(id) ?? -Infinity, start));
    if (start < duration && Number.isFinite(end)) tails.set(id, Math.max(tails.get(id) ?? -Infinity, end));
  }

  const results = new Map<string, IntervalResult>();
  for (const { ranges } of index.intervalsByTrack.values()) {
    for (const { value } of ranges) results.set(value.interval.id, value.result);
  }
  const statuses = new Map<string, StatusRange[]>();
  const gapEvidence = new Map<string, DecodedGapEvidence>();
  for (const interval of intervals) {
    const source = index.byId.get(interval.start.id);
    const target = index.byId.get(interval.end.id);
    const id = source?.interactionId ?? source?.id;
    if (!id || !coverage.has(id) || id !== (target?.interactionId ?? target?.id)) continue;
    const error = errors.get(interval.id) ?? terminalReason;
    const active = activity.active.get(interval.id);
    const result = results.get(interval.id);
    const complete = activity.completed.has(interval.id) && result?.phase !== "sparse";
    const recovering = complete && isRecoveryPending(activity.recovery?.intervals.get(interval.id));
    const status: Status = error
      ? { state: "unavailable", reason: error }
      : recovering
        ? { state: "pending", reason: "Recovering missing boxes with the model." }
        : complete
          ? { state: "unavailable", reason: "No accepted tracking prediction." }
          : { state: "pending", reason: active ? STAGES[active.stage] : "Queued for tracking." };
    const ranges = statuses.get(id) ?? [];
    // The following interval owns shared GT pixels. Only the last interval
    // covers its cluster's final raw display tail while processing continues.
    const end =
      interval.end.time === lastAnchors.get(id)
        ? Math.max(interval.end.time, tails.get(id) ?? interval.end.time)
        : interval.end.time;
    ranges.push({ start: interval.start.time, end, ...status, keepReady: recovering });
    // Existing local results stay solid. New recovery boxes wait for refinement
    // before revealing their timeline coverage, avoiding isolated solid slivers.
    if (recovering && result) {
      for (const frame of result.frames) {
        if (frame.recovery)
          ranges.push({
            start: Math.max(interval.start.time, frame.timestamp),
            end: Math.min(end, frame.timestamp + frame.duration),
            state: "pending",
            reason: "Refining recovered boxes.",
          });
      }
    }
    statuses.set(id, ranges);
    if (
      complete &&
      result &&
      activity.enabled &&
      !activity.unavailableReason &&
      !errors.has("worker") &&
      !errors.has(interval.id)
    ) {
      const evidence = gapEvidence.get(id) ?? new DecodedGapEvidence();
      evidence.add(result.frames, interval.start.time, end);
      gapEvidence.set(id, evidence);
    }
  }

  const segments = new Map<string, TimelineTrackSegment>();
  for (const segment of collectRenderableSegments(items)) {
    const ready = coverage.get(segment.id);
    if (!ready || segment.type !== "point_box") continue;
    // Raw GT timing determines the envelope, including its final visibility tail.
    // Decoded frame PTS/durations must never move these outer edges as work arrives.
    const start = Math.min(duration, Math.max(0, segment.start));
    const end = Math.min(duration, Math.max(start, segment.end ?? segment.start, tails.get(segment.id) ?? start));
    const runs =
      Number.isFinite(start) && start < end
        ? projectRuns(start, end, ready, statuses.get(segment.id) ?? [], fallback)
        : [];
    const evidence = gapEvidence.get(segment.id);
    const displayedRuns = runs.map((run, i) => {
      if (run.state !== "unavailable" || runs[i - 1]?.state !== "ready" || runs[i + 1]?.state !== "ready") return run;
      const count = evidence?.count(run.start, run.end);
      return count === undefined
        ? run
        : {
            ...run,
            state: "bridged" as const,
            reason: `Display-only bridge across ${count} missing decoded frame${count === 1 ? "" : "s"}.`,
          };
    });
    segments.set(segment.id, { start, end, runs: displayedRuns });
  }
  return { coverage, segments };
}

/** Merge chronological ready/pending runs; terminal gaps remain absent from the displayed timeline. */
export function visibleTimelineRanges(segment: TimelineTrackSegment): readonly { start: number; end: number }[] {
  const ranges: { start: number; end: number }[] = [];
  for (const run of segment.runs) {
    if (run.state === "unavailable") continue;
    const start = Math.max(segment.start, run.start);
    const end = Math.min(segment.end, run.end);
    if (!(start < end)) continue;
    const previous = ranges.at(-1);
    if (previous && start <= previous.end) previous.end = Math.max(previous.end, end);
    else ranges.push({ start, end });
  }
  return ranges;
}

/** Rows, summaries and navigation share visible fragments while retaining the logical cluster identity. */
export function projectTimelineSegments(
  segments: readonly TRenderableSegment[],
  timeline: TrackingTimeline | null,
): TRenderableSegment[] {
  return segments.flatMap((segment) => {
    const envelope = timeline?.segments.get(segment.id);
    if (!envelope || segment.type !== "point_box") return [segment];
    return visibleTimelineRanges(envelope).map((visibleRange) => ({ ...segment, ...visibleRange, visibleRange }));
  });
}
